<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    icon="fa-eye"
    sizeClass="modal-xxl modal-doc-preview"
    :showFooter="false"
  >
    <Spinner v-if="loading" />
    <div class="preview-container">
      <div class="text-center text-danger" v-if="loadingError">{{loadingError}}</div>

      <div class="preview-container-thumbnail-container" v-if="previewDocumentList.length > 0">
        <div class="preview-container-thumbnails">
          <div v-for="file of previewDocumentList" :key="file.id" :class="{'document-thumb': true, 'active': previewActiveDocumentId == file.id}" @click.prevent="selectDocument(file)">
            <i :class="['far', iconForFile(file)]"></i> <span class="title">{{file.name}}</span>
          </div>
        </div>
      </div>

      <div class="preview-main">
        <div class="preview-details" v-if="!loading && activeDocument">
          <ActionDropMenu class="float-right">
            <DropdownMenuItem icon="fa-download" :link="activeDocument.url" @click="openDocument(activeDocument)">Open in new tab</DropdownMenuItem>
          </ActionDropMenu>
          <h4>{{docTypeName(activeDocument)}}</h4>
          <div class="row no-gutters">
            <div class="col-6">
              <div class="row no-gutters document-properties">
                <div class="col-2 font-weight-bold">Filename</div>
                <div class="col-10">{{activeDocument.name}}</div>
              </div>
              <div class="row no-gutters document-properties" v-if="activeDocument.description">
                <div class="col-2 font-weight-bold">Description:</div>
                <div class="col-10">{{activeDocument.description}}</div>
              </div>
            </div>
            <div class="col-6">
              <div class="row no-gutters document-properties" v-if="activeDocument.createdAt">
                <div class="col-2 font-weight-bold">Uploaded:</div>
                <div class="col-10">{{formatDateTime(activeDocument.createdAt)}}</div>
              </div>
              <div class="row no-gutters document-properties" v-if="activeDocument.user">
                <div class="col-2 font-weight-bold">User:</div>
                <div class="col-10"><LinkUser :id="activeDocument.user.id" :name="activeDocument.user.name" /></div>
              </div>
            </div>
          </div>
        </div>

        <div class="preview-content">
          <Spinner v-if="loadingDocument" />
          <div class="text-center text-danger" v-if="loadingDocError">{{loadingDocError}}</div>

          <template v-if="!loading && !loadingDocument">
            <SVGPreview :svgData="tempSvgData" v-if="isDXF" />
            <DocumentPreview :url="activeDocument.url" v-if="activeDocument && isPreviewAvailable" />
            <h2 v-if="activeDocument && !isPreviewAvailable && !isDXF" class="text-center">
              Unknown file type. No preview available
            </h2>
            <h2 v-if="!activeDocument && !hasDocuments" class="text-center">
              No documents attached
            </h2>
          </template>
        </div>
      </div>

      
    </div>
  </ModalDialog>
</template>

<style>
.preview-container {
  padding: 0;
  margin: 0;
  width: 100%;
  height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.preview-main {
  position: relative;
  flex: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.preview-content {
  position: relative;
  flex: 1;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 1px solid #808080;
  background-color: #d4d4d4;
}
.theme-dark .preview-content {
  border: 1px solid #202020;
  background-color: #303030;
}
.preview-details {
  height: 80px;
  max-height: 80px;
  padding: 4px;
  overflow: hidden;
}
.preview-details .document-properties {
  font-size: 11px;
  margin: 1px 8px;
  color: #808080;
}

.preview-container .preview-content > iframe,
.preview-container .preview-content > div {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  margin: 0;
  padding: 0;
}
.preview-container div.preview-container-thumbnail-container {
  position: relative;
  width: 380px;
}
.preview-container div.preview-container-thumbnails {
  position: absolute;
  top: 8px;
  left: 0;
  bottom: 4px;
  right: 0;
  text-align: left;
  padding: 8px 0px 4px 0px;
  overflow-y: scroll;
}
.preview-container-thumbnails > .document-thumb {
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #c7c7c7;
  display: inline-block;
  color: inherit;
  padding: 5px;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
  width: 100%;
}

.preview-container-thumbnails > .document-thumb.active {
  background-color: #a7aaac;
  border: 1px solid #676a6c;
  color: #ffffff;
}

.theme-dark .preview-container-thumbnails > .document-thumb {
  background-color: #505050;
  border: 1px solid #808080;
  color: #a0a0a0;
}
.theme-dark .preview-container-thumbnails > .document-thumb.active {
  background-color: #808080;
  border: 1px solid #ffffff;
  color: #ffffff;
}

.modal-doc-preview .modal-body {
  padding: 5px;
}
</style>

<script>
import _ from 'lodash'
import userFileMixin from '@/mixins/userFile'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'
import DocumentPreview from '@/components/documentPreview'

export default {
  name: 'DocumentPreviewDialog',
  mixins: [
    modalDialog,
    userFileMixin
  ],
  components: {
    ModalDialog,
    DocumentPreview
  },
  props: {
    title: {
      type: String,
      default: 'Document Preview'
    },
    // Either a UserFile document list, linkId/linkType or an upload waiting to be processed
    documentList: Array,
    currentDocumentId: Number,

    linkId: Number,
    linkType: String,

    uploadFile: Object,     // Upload
    uploadList: Array,      // List of uploads
  },
  data() {
    return {
      loading: true,
      loadingError: '',
      tempSvgData: '',

      loadingDocument: false,
      loadingDocError: '',
      
      previewDocumentList: _.cloneDeep(this.documentList || []),
      previewActiveDocumentId: this.currentDocumentId
    }
  },
  mounted() {
    this.loading = false

    // Load document list if linkId/linkType provided
    if(this.linkId && this.linkType) {
      this.requestDocumentList()
    } else {
      if(_.isArray(this.documentList) && this.documentList.length > 0) {
        this.previewDocumentList = _.cloneDeep(this.documentList || [])
        this.previewActiveDocumentId = this.currentDocumentId
      }

      // Uploads. Used to display uploads that are pending processing into UserFiles
      // Convert to document (UserFile) type structure
      if(_.isArray(this.uploadList) && this.uploadList.length > 0) {
        this.previewDocumentList = _.cloneDeep(this.uploadList || []).map(upload => {
          return {
            id: upload.key,
            name: upload.name,
            mimeType: upload.mimeType,
            url: upload.url
          }
        })
      }

      // Single upload
      if(this.previewDocumentList.length == 0 && this.uploadFile) {
        this.previewDocumentList = [this.uploadFile].map(upload => {
          return {
            id: upload.key,
            name: upload.name,
            mimeType: upload.mimeType,
            url: upload.url
          }
        })
      }

      if(this.previewDocumentList.find(doc => doc.id == this.previewActiveDocumentId) == null && this.previewDocumentList.length > 0)
        this.previewActiveDocumentId = this.previewDocumentList[0].id
    }

    if(this.activeDocument)
      this.selectDocument(this.activeDocument)
  },
  computed: {
    hasDocuments() {
      return this.previewDocumentList && this.previewDocumentList.length > 0
    },
    activeDocument() {
      return this.previewDocumentList && this.previewDocumentList.find(doc => doc.id == this.previewActiveDocumentId)
    },
    isPreviewAvailable() {
      if(!this.activeDocument)
        return false
      if(this.isDXF)
        return false

      var dataMimeTypes = [
        'application/octet-stream',
        'application/gzip',
        'application/zip'
      ]

      // Don't allow preview for octet streams
      if(dataMimeTypes.includes(this.activeDocument.mimeType.toLowerCase()))
        return false

      return true
    },
    isDXF() {
      if(!this.activeDocument)
        return false
      if(_.endsWith(this.activeDocument.name.trim().toLowerCase(), '.dxf'))
        return true
      return false
    },
    isImage() {
      if(!this.activeDocument)
        return false
      let filename = this.activeDocument.name.trim().toLowerCase()
      let extensionList = ['jpg', 'jpeg', 'png']
      if(extensionList.filter(ext => _.endsWith(filename, `.${ext}`)).length > 0)
        return true
      return false
    }
  },
  methods: {
    show(params) {
      return modalDialog.methods.show.call(this, params)
    },
    onClosed() {
      this.modalClosed(false)
    },

    requestDocumentList() {
      this.loadingError = ''
      this.loading = true
      this.$apiService.coreService.getUserFiles(this.linkType, this.linkId)
      .then(response => {
        this.previewDocumentList = response.data
        if(!this.previewActiveDocumentId && this.previewDocumentList.length > 0) {

          this.selectDocument(this.previewDocumentList[0])
        }
      })
      .catch(err => {
        this.loadingError = 'Error loading document list'
      })
      .finally(() => {
        this.loading = false
      })
    },
    selectDocument(document) {
      // Reset
      this.loadingDocument = false
      this.loadingDocError = ''

      this.previewActiveDocumentId = document.id

      // Check for mimetype / extension to figure out how to best display the document
      if(this.isDXF) {
        this.loadingDocument = true
        this.loadingDocError = ''

        var data = {}
        if(this.uploadFile)
          data.s3UploadKey = this.uploadFile.key
        else
          data.userFileId = this.previewActiveDocumentId

        this.$apiService.coreService.convertDXFFile(data)
        .then(response => {
          this.tempSvgData = response.data.svgData
        })
        .catch(err => {
          this.loadingDocError = 'Error converting DXF'
        })
        .finally(() => {
          this.loadingDocument = false
        })
      }
    },
    iconForFile(document) {
      return this.iconForUserFile(document.mimeType)
    },
    docTypeName(document) {
      return this.descriptionForUserFileMimeType(document.mimeType, document.name)
    },
    openDocument(document) {
      window.open(document.url, '_blank').focus()
    }
  }
}
</script>